<template>
  <div class="config-container" :class="adminDarkClass">
    <el-card class="config-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-monitor"></i> {{ pageTitle }}</span>
        <div class="header-actions">
          <el-button type="primary" size="small" @click="saveConfig" :loading="saving">
            <i class="el-icon-check"></i> 保存配置
          </el-button>
          <el-button size="small" @click="loadConfig">
            <i class="el-icon-refresh"></i> 重置
          </el-button>
        </div>
      </div>

      <el-tabs v-model="activeTab" type="border-card" class="config-tabs">
        <!-- 主题配置 -->
        <el-tab-pane label="主题配置" name="theme">
          <div class="tab-icon"><i class="el-icon-brush"></i></div>
          <el-form :model="configForm" label-width="180px" class="config-form">
            <el-form-item label="快速选择主题预设">
              <div class="theme-presets">
                <div 
                  v-for="preset in themePresets" 
                  :key="preset.name"
                  class="preset-item"
                  :class="{ 'active': isCurrentPreset(preset) }"
                  @click="applyPreset(preset)">
                  <div class="preset-preview" :style="{ background: preset.preview }">
                    <i :class="preset.icon"></i>
                  </div>
                  <div class="preset-info">
                    <div class="preset-name">{{ preset.name }}</div>
                    <div class="preset-desc">{{ preset.desc }}</div>
                  </div>
                  <i v-if="isCurrentPreset(preset)" class="el-icon-check preset-check"></i>
                </div>
              </div>
              <div class="form-tip">点击任意主题预设可快速应用对应的主题配置</div>
            </el-form-item>

            <el-divider content-position="left">自定义主题</el-divider>

            <el-form-item label="主题模式">
              <el-radio-group v-model="configForm.theme_mode">
                <el-radio label="light">
                  <i class="el-icon-sunny"></i> 浅色模式
                </el-radio>
                <el-radio label="dark">
                  <i class="el-icon-moon"></i> 深色模式
                </el-radio>
              </el-radio-group>
              <div class="form-tip">浅色模式适合白天使用，深色模式适合夜间使用</div>
            </el-form-item>

            <el-form-item label="主色调" v-if="configForm.theme_mode === 'light'">
              <el-color-picker v-model="configForm.theme_primary_color" show-alpha></el-color-picker>
              <span class="color-value">{{ configForm.theme_primary_color }}</span>
              <div class="form-tip">设置浅色模式下的主色调，影响导航栏、按钮等元素</div>
            </el-form-item>

            <el-form-item label="背景色" v-if="configForm.theme_mode === 'dark'">
              <el-color-picker v-model="configForm.theme_dark_bg_color" show-alpha></el-color-picker>
              <span class="color-value">{{ configForm.theme_dark_bg_color }}</span>
              <div class="form-tip">深色模式下的背景颜色</div>
            </el-form-item>

            <el-form-item label="文字色" v-if="configForm.theme_mode === 'dark'">
              <el-color-picker v-model="configForm.theme_dark_text_color" show-alpha></el-color-picker>
              <span class="color-value">{{ configForm.theme_dark_text_color }}</span>
              <div class="form-tip">深色模式下的文字颜色</div>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 显示配置 -->
        <el-tab-pane label="显示配置" name="display">
          <div class="tab-icon"><i class="el-icon-monitor"></i></div>
          <el-form :model="configForm" label-width="180px" class="config-form">
            <el-form-item label="资源显示模式">
              <el-radio-group v-model="configForm.resource_display_mode">
                <el-radio label="list">
                  <i class="el-icon-s-operation"></i> 列表式
                </el-radio>
                <el-radio label="grid">
                  <i class="el-icon-s-grid"></i> 网格式
                </el-radio>
              </el-radio-group>
              <div class="form-tip">列表式：横向排列，信息丰富；网格式：卡片展示，视觉效果好</div>
            </el-form-item>

            <el-form-item label="列表列数" v-if="configForm.resource_display_mode === 'list'">
              <el-radio-group v-model.number="listColumns">
                <el-radio :label="1">单列布局</el-radio>
                <el-radio :label="2">双列布局</el-radio>
              </el-radio-group>
              <div class="form-tip">单列布局信息更详细，双列布局更紧凑</div>
            </el-form-item>

            <el-form-item label="网格列数" v-if="configForm.resource_display_mode === 'grid'">
              <el-slider 
                v-model.number="gridColumns" 
                :min="2" 
                :max="6" 
                :marks="marks"
                show-stops>
              </el-slider>
              <div class="form-tip">设置网格模式下每行显示的资源卡片数量（2-6个）</div>
            </el-form-item>

            <!-- 预览效果 -->
            <el-divider content-position="left">显示效果预览</el-divider>
            <div class="preview-container">
              <div v-if="configForm.resource_display_mode === 'list'" class="preview-list" :class="{ 'two-columns': listColumns === 2 }">
                <div class="preview-item" v-for="i in (listColumns === 2 ? 4 : 2)" :key="i">
                  <div class="preview-header">
                    <div class="preview-title">资源标题示例</div>
                    <div class="preview-tags">
                      <span class="preview-tag">免费</span>
                      <span class="preview-tag">软件</span>
                    </div>
                  </div>
                  <div class="preview-body">
                    <div class="preview-cover"></div>
                    <div class="preview-content">
                      <div class="preview-desc">这是资源的描述信息...</div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="preview-grid" :style="{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }">
                <div class="preview-card" v-for="i in gridColumns * 2" :key="i">
                  <div class="preview-card-cover"></div>
                  <div class="preview-card-title">资源标题</div>
                </div>
              </div>
            </div>
          </el-form>
        </el-tab-pane>

        <!-- Logo配置 -->
        <el-tab-pane label="Logo配置" name="logo">
          <div class="tab-icon"><i class="el-icon-picture-outline"></i></div>
          <el-form :model="configForm" label-width="180px" class="config-form">
            <el-form-item label="网站Logo">
              <div class="logo-upload-area">
                <div class="logo-preview-box" v-if="configForm.logo_url">
                  <img :src="configForm.logo_url" class="logo-preview">
                  <div class="logo-actions">
                    <el-button type="primary" size="mini" @click="openLogoCropper">
                      <i class="el-icon-edit"></i> 重新上传
                    </el-button>
                    <el-button type="danger" size="mini" @click="removeLogo">
                      <i class="el-icon-delete"></i> 移除
                    </el-button>
                  </div>
                </div>
                <div v-else class="logo-upload-btn" @click="openLogoCropper">
                  <i class="el-icon-plus"></i>
                  <div class="upload-text">点击上传Logo</div>
                  <div class="upload-hint">支持JPG/PNG格式，推荐PNG透明背景</div>
                </div>
              </div>
              <div class="form-tip">建议尺寸：宽度300-400像素，高度80-120像素，比例3:1最佳。<strong style="color: #67C23A;">支持PNG透明背景，推荐使用透明背景PNG格式</strong>，也支持JPG格式</div>
            </el-form-item>

            <el-form-item label="网站名称">
              <el-input v-model="configForm.site_name" placeholder="例如：知享汇资源库" maxlength="50">
                <i slot="prefix" class="el-icon-edit"></i>
              </el-input>
              <div class="form-tip">显示在网站顶部的名称</div>
            </el-form-item>

            <el-form-item label="网站口号">
              <el-input v-model="configForm.site_slogan" placeholder="例如：Share & Discover" maxlength="100">
                <i slot="prefix" class="el-icon-edit"></i>
              </el-input>
              <div class="form-tip">显示在网站名称下方的宣传语</div>
            </el-form-item>

            <el-form-item label="网站标题">
              <el-input v-model="configForm.site_title" placeholder="例如：知享汇 - 优质资源分享平台" maxlength="100">
                <i slot="prefix" class="el-icon-s-flag"></i>
              </el-input>
              <div class="form-tip">显示在浏览器标签页的标题（HTML title标签），留空则使用网站名称</div>
            </el-form-item>

            <el-form-item label="网站地址">
              <el-input v-model="configForm.site_url" placeholder="例如：https://www.example.com" maxlength="200">
                <i slot="prefix" class="el-icon-link"></i>
              </el-input>
              <div class="form-tip">网站的永久访问地址，用于版权信息中显示</div>
            </el-form-item>

            <el-form-item label="网站备案号">
              <el-input v-model="configForm.site_icp" placeholder="例如：京ICP备12345678号" maxlength="100">
                <i slot="prefix" class="el-icon-document"></i>
              </el-input>
              <div class="form-tip">网站备案号，显示在网站底部，留空则不显示</div>
            </el-form-item>

            <el-divider content-position="left">Logo预览</el-divider>
            <div class="logo-preview-container">
              <div class="logo-preview-header">
                <div class="preview-logo-wrapper">
                  <img v-if="configForm.logo_url" :src="configForm.logo_url" alt="Logo" class="preview-logo-img">
                  <div v-else class="preview-logo-placeholder">Logo</div>
                  <div class="preview-logo-text">
                    <span class="preview-site-name">{{ configForm.site_name || '知享汇资源库' }}</span>
                    <span class="preview-site-slogan">{{ configForm.site_slogan || 'Share & Discover' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </el-form>
        </el-tab-pane>

        <!-- 联系方式配置 -->
        <el-tab-pane label="联系方式" name="contact">
          <div class="tab-icon"><i class="el-icon-message"></i></div>
          <el-form :model="contactConfig" label-width="180px" class="config-form">
            <el-row :gutter="40">
              <el-col :span="12">
                <el-form-item label="微信二维码">
                  <el-upload
                    class="qrcode-uploader"
                    action="/api/upload/image"
                    :headers="uploadHeaders"
                    :show-file-list="false"
                    :on-success="handleWechatQrSuccess"
                    :before-upload="beforeQrUpload">
                    <img v-if="contactConfig.wechatQrCode" :src="contactConfig.wechatQrCode" class="qrcode-preview">
                    <div v-else class="upload-placeholder">
                      <i class="el-icon-plus"></i>
                      <div class="upload-text">点击上传</div>
                    </div>
                  </el-upload>
                  <div class="form-tip">建议尺寸：120x120像素，支持JPG/PNG格式</div>
                </el-form-item>

                <el-form-item label="微信二维码描述">
                  <el-input v-model="contactConfig.wechatDesc" placeholder="例如：联系站长" maxlength="50">
                    <i slot="prefix" class="el-icon-edit"></i>
                  </el-input>
                  <div class="form-tip">显示在微信二维码下方的提示文字</div>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="公众号二维码">
                  <el-upload
                    class="qrcode-uploader"
                    action="/api/upload/image"
                    :headers="uploadHeaders"
                    :show-file-list="false"
                    :on-success="handleOfficialQrSuccess"
                    :before-upload="beforeQrUpload">
                    <img v-if="contactConfig.officialQrCode" :src="contactConfig.officialQrCode" class="qrcode-preview">
                    <div v-else class="upload-placeholder">
                      <i class="el-icon-plus"></i>
                      <div class="upload-text">点击上传</div>
                    </div>
                  </el-upload>
                  <div class="form-tip">建议尺寸：120x120像素，支持JPG/PNG格式</div>
                </el-form-item>

                <el-form-item label="公众号二维码描述">
                  <el-input v-model="contactConfig.officialDesc" placeholder="例如：关注更新" maxlength="50">
                    <i slot="prefix" class="el-icon-edit"></i>
                  </el-input>
                  <div class="form-tip">显示在公众号二维码下方的提示文字</div>
                </el-form-item>
              </el-col>
            </el-row>

            <el-divider></el-divider>

            <el-row :gutter="40">
              <el-col :span="12">
                <el-form-item label="联系邮箱">
                  <el-input v-model="contactConfig.email" placeholder="例如：admin@example.com" maxlength="100">
                    <i slot="prefix" class="el-icon-message"></i>
                  </el-input>
                  <div class="form-tip">展示在网站底部的联系邮箱</div>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="联系电话">
                  <el-input v-model="contactConfig.phone" placeholder="例如：400-888-8888" maxlength="50">
                    <i slot="prefix" class="el-icon-phone"></i>
                  </el-input>
                  <div class="form-tip">展示在网站底部的商务合作电话</div>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- Logo裁剪对话框 -->
    <el-dialog
      title="上传Logo图片"
      :visible.sync="logoCropperVisible"
      width="900px"
      :close-on-click-modal="false"
      @close="handleCropperClose">
      <div class="cropper-container">
        <div class="cropper-main">
          <div class="cropper-area">
            <vue-cropper
              ref="cropper"
              :img="cropperOption.img"
              :output-size="cropperOption.outputSize"
              :output-type="cropperOption.outputType"
              :info="true"
              :can-scale="true"
              :auto-crop="true"
              :auto-crop-width="cropperOption.autoCropWidth"
              :auto-crop-height="cropperOption.autoCropHeight"
              :fixed="true"
              :fixed-number="cropperOption.fixedNumber"
              :full="false"
              :can-move="true"
              :can-move-box="true"
              :original="false"
              :center-box="true"
              :high="true"
              :info-true="true"
              mode="contain"
              @real-time="handleRealTime">
            </vue-cropper>
          </div>
          <div class="cropper-tools">
            <el-button-group>
              <el-button size="small" icon="el-icon-zoom-in" @click="changeScale(1)">放大</el-button>
              <el-button size="small" icon="el-icon-zoom-out" @click="changeScale(-1)">缩小</el-button>
              <el-button size="small" icon="el-icon-refresh-left" @click="rotateLeft">左旋转</el-button>
              <el-button size="small" icon="el-icon-refresh-right" @click="rotateRight">右旋转</el-button>
            </el-button-group>
          </div>
        </div>
        <div class="cropper-preview-area">
          <div class="preview-title">
            <i class="el-icon-view"></i> 裁剪预览
          </div>
          <div class="preview-box">
            <div class="preview-wrapper" :style="previewStyle">
              <div :style="previewStyle.div">
                <img :src="previewStyle.url" :style="previewStyle.img">
              </div>
            </div>
          </div>
          <div class="preview-info">
            <p><i class="el-icon-picture"></i> 建议尺寸：{{ cropperOption.autoCropWidth }}x{{ cropperOption.autoCropHeight }}像素</p>
            <p><i class="el-icon-coordinate"></i> 比例锁定：{{ cropperOption.fixedNumber[0] }}:{{ cropperOption.fixedNumber[1] }}</p>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="logoCropperVisible = false">取 消</el-button>
        <el-button type="primary" @click="uploadCroppedImage" :loading="uploading">
          <i class="el-icon-upload"></i> 确定上传
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { VueCropper } from 'vue-cropper'

export default {
  name: 'SystemConfig',
  components: {
    VueCropper
  },
  data() {
    return {
      pageTitle: '前端系统配置', // 默认标题
      activeTab: 'theme',
      isAdminDark: this.getInitialAdminDarkMode(),
      themePresets: [
        {
          name: '默认紫色',
          desc: '优雅的紫色渐变主题',
          icon: 'el-icon-star-on',
          preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          config: {
            theme_mode: 'light',
            theme_primary_color: '#667eea',
            theme_dark_bg_color: '#1a1a1a',
            theme_dark_text_color: '#e0e0e0'
          }
        },
        {
          name: '蓝色海洋',
          desc: '清新的蓝色主题',
          icon: 'el-icon-water-cup',
          preview: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          config: {
            theme_mode: 'light',
            theme_primary_color: '#4facfe',
            theme_dark_bg_color: '#1a1a1a',
            theme_dark_text_color: '#e0e0e0'
          }
        },
        {
          name: '日落橙色',
          desc: '温暖的橙色主题',
          icon: 'el-icon-sunny',
          preview: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          config: {
            theme_mode: 'light',
            theme_primary_color: '#fa709a',
            theme_dark_bg_color: '#1a1a1a',
            theme_dark_text_color: '#e0e0e0'
          }
        },
        {
          name: '森林绿色',
          desc: '自然的绿色主题',
          icon: 'el-icon-grape',
          preview: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)',
          config: {
            theme_mode: 'light',
            theme_primary_color: '#0ba360',
            theme_dark_bg_color: '#1a1a1a',
            theme_dark_text_color: '#e0e0e0'
          }
        },
        {
          name: '午夜深色',
          desc: '护眼的深色主题',
          icon: 'el-icon-moon',
          preview: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          config: {
            theme_mode: 'dark',
            theme_primary_color: '#667eea',
            theme_dark_bg_color: '#1a1a1a',
            theme_dark_text_color: '#e0e0e0'
          }
        },
        {
          name: '深邃黑色',
          desc: 'OLED优化的纯黑主题',
          icon: 'el-icon-moon-night',
          preview: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
          config: {
            theme_mode: 'dark',
            theme_primary_color: '#667eea',
            theme_dark_bg_color: '#000000',
            theme_dark_text_color: '#e8e8e8'
          }
        }
      ],
      configForm: {
        theme_mode: 'light',
        theme_primary_color: '#667eea',
        theme_dark_bg_color: '#1a1a1a',
        theme_dark_text_color: '#e0e0e0',
        resource_display_mode: 'list',
        resource_list_columns: '1',
        resource_grid_columns: '3',
        logo_url: '',
        site_name: '知享汇资源库',
        site_slogan: 'Share & Discover',
        site_title: '',  // 网站标题，用于HTML title标签
        site_url: '',  // 网站地址
        site_icp: ''  // 网站备案号
      },
      contactConfig: {
        wechatQrCode: '',
        officialQrCode: '',
        email: '',
        phone: '',
        wechatDesc: '',
        officialDesc: ''
      },
      uploadHeaders: {
        Authorization: `Bearer ${localStorage.getItem('admin_token')}`
      },
      saving: false,
      // Logo裁剪相关
      logoCropperVisible: false,
      uploading: false,
      cropperOption: {
        img: '',
        outputSize: 1,
        outputType: 'png',  // 使用PNG格式保持透明通道
        autoCropWidth: 360,
        autoCropHeight: 120,
        fixedNumber: [3, 1],
        canMove: true,
        canScale: true,
        autoCrop: true,
        centerBox: true,
        high: true,  // 高清模式
        original: true  // 保持原图像质量
      },
      previewStyle: {
        url: '',
        img: {},
        div: {}
      },
      marks: {
        2: '2',
        3: '3',
        4: '4',
        5: '5',
        6: '6'
      }
    }
  },
  computed: {
    adminDarkClass() {
      return this.isAdminDark ? 'admin-dark-mode' : ''
    },
    listColumns: {
      get() {
        return parseInt(this.configForm.resource_list_columns)
      },
      set(val) {
        this.configForm.resource_list_columns = String(val)
      }
    },
    gridColumns: {
      get() {
        return parseInt(this.configForm.resource_grid_columns)
      },
      set(val) {
        this.configForm.resource_grid_columns = String(val)
      }
    }
  },
  mounted() {
    this.loadPageTitle()
    this.loadConfig()
    this.checkAdminTheme()
    
    // 监听管理后台主题变化
    this.$root.$on('admin-theme-change', (theme) => {
      this.isAdminDark = theme.adminThemeMode === 'dark'
    })
  },
  beforeDestroy() {
    this.$root.$off('admin-theme-change')
  },
  methods: {
    // 动态加载页面标题
    async loadPageTitle() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allMenus = menus.menus || []
          const currentMenu = allMenus.find(m => m.url === this.$route.path)
          if (currentMenu) {
            this.pageTitle = currentMenu.name
          }
        }
      } catch (error) {
        console.error('加载页面标题失败:', error)
      }
    },
    // 从 localStorage 获取初始管理后台深色模式状态
    getInitialAdminDarkMode() {
      const cachedTheme = localStorage.getItem('admin_theme_config')
      if (cachedTheme) {
        try {
          const theme = JSON.parse(cachedTheme)
          return theme.adminThemeMode === 'dark'
        } catch (e) {
          return false
        }
      }
      return false
    },
    async checkAdminTheme() {
      try {
        const res = await this.$axios.get('/admin/config/admin-theme')
        if (res.data.code === 200 && res.data.data) {
          this.isAdminDark = res.data.data.adminThemeMode === 'dark'
        }
      } catch (error) {
        console.error('检查管理后台主题失败:', error)
      }
    },
    async loadConfig() {
      const res = await this.$axios.get('/admin/config/map')
      if (res.data.code === 200) {
        const configMap = res.data.data
        this.configForm.theme_mode = configMap.theme_mode || 'light'
        this.configForm.theme_primary_color = configMap.theme_primary_color || '#667eea'
        this.configForm.theme_dark_bg_color = configMap.theme_dark_bg_color || '#1a1a1a'
        this.configForm.theme_dark_text_color = configMap.theme_dark_text_color || '#e0e0e0'
        this.configForm.resource_display_mode = configMap.resource_display_mode || 'list'
        this.configForm.resource_list_columns = configMap.resource_list_columns || '1'
        this.configForm.resource_grid_columns = configMap.resource_grid_columns || '3'
        this.configForm.logo_url = configMap.logo_url || ''
        this.configForm.site_name = configMap.site_name || '知享汇资源库'
        this.configForm.site_slogan = configMap.site_slogan || 'Share & Discover'
        this.configForm.site_title = configMap.site_title || ''
        this.configForm.site_url = configMap.site_url || ''
        this.configForm.site_icp = configMap.site_icp || ''
      }
      
      // 加载联系方式配置
      const contactRes = await this.$axios.get('/admin/config/contact')
      if (contactRes.data.code === 200) {
        const contact = contactRes.data.data
        this.contactConfig = {
          wechatQrCode: contact.wechatQrCode || '',
          officialQrCode: contact.officialQrCode || '',
          email: contact.email || '',
          phone: contact.phone || '',
          wechatDesc: contact.wechatDesc || '',
          officialDesc: contact.officialDesc || ''
        }
      }
    },
    async saveConfig() {
      this.saving = true
      try {
        // 保存系统配置
        await this.$axios.put('/admin/config/batch', this.configForm)
        
        // 保存联系方式配置
        await this.$axios.put('/admin/config/contact', this.contactConfig)
        
        this.$message({
          type: 'success',
          dangerouslyUseHTMLString: true,
          message: `
            <div style="line-height: 1.6;">
              <strong>配置保存成功！</strong><br/>
              <span style="font-size: 12px; color: #909399;">
                · 前端用户刷新页面即可看到新主题<br/>
                · 用户手动切换的深色模式在24小时后失效
              </span>
            </div>
          `,
          duration: 6000,
          showClose: true
        })
      } catch (error) {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    beforeQrUpload(file) {
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('二维码图片只能是JPG或PNG格式!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('二维码图片大小不能超过2MB!')
        return false
      }
      return true
    },
    handleWechatQrSuccess(response) {
      if (response.code === 200) {
        this.contactConfig.wechatQrCode = response.data.url
        this.$message.success('微信二维码上传成功')
      } else {
        this.$message.error('上传失败')
      }
    },
    handleOfficialQrSuccess(response) {
      if (response.code === 200) {
        this.contactConfig.officialQrCode = response.data.url
        this.$message.success('公众号二维码上传成功')
      } else {
        this.$message.error('上传失败')
      }
    },

    // Logo裁剪相关方法
    openLogoCropper() {
      // 创建隐藏的文件输入框
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/jpeg,image/png'
      input.onchange = (e) => {
        const file = e.target.files[0]
        if (!file) return

        // 验证文件类型和大小
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
        const isLt5M = file.size / 1024 / 1024 < 5

        if (!isImage) {
          this.$message.error('Logo图片只能是JPG或PNG格式!')
          return
        }
        if (!isLt5M) {
          this.$message.error('Logo图片大小不能超过5MB!')
          return
        }

        // 读取图片
        const reader = new FileReader()
        reader.onload = (e) => {
          this.cropperOption.img = e.target.result
          this.logoCropperVisible = true
        }
        reader.readAsDataURL(file)
      }
      input.click()
    },

    removeLogo() {
      this.$confirm('确定要移除Logo吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.configForm.logo_url = ''
        this.$message.success('Logo已移除')
      }).catch(() => {})
    },

    handleCropperClose() {
      this.cropperOption.img = ''
      this.previewStyle = { url: '', img: {}, div: {} }
    },

    handleRealTime(data) {
      this.previewStyle = data
    },

    changeScale(num) {
      num = num || 1
      this.$refs.cropper.changeScale(num)
    },

    rotateLeft() {
      this.$refs.cropper.rotateLeft()
    },

    rotateRight() {
      this.$refs.cropper.rotateRight()
    },

    uploadCroppedImage() {
      this.$refs.cropper.getCropBlob((blob) => {
        const formData = new FormData()
        formData.append('file', blob, 'logo.png')

        this.uploading = true
        this.$axios.post('/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...this.uploadHeaders
          }
        }).then(response => {
          if (response.data.code === 200) {
            this.configForm.logo_url = response.data.data.url
            this.$message.success('Logo上传成功')
            this.logoCropperVisible = false
          } else {
            this.$message.error('上传失败：' + (response.data.message || '未知错误'))
          }
        }).catch(error => {
          console.error('Logo上传失败:', error)
          this.$message.error('上传失败，请检查网络连接')
        }).finally(() => {
          this.uploading = false
        })
      })
    },

    beforeLogoUpload(file) {
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('Logo图片只能是JPG或PNG格式!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('Logo图片大小不能超过2MB!')
        return false
      }
      return true
    },
    handleLogoSuccess(response) {
      if (response.code === 200) {
        this.configForm.logo_url = response.data.url
        this.$message.success('Logo上传成功')
      } else {
        this.$message.error('上传失败')
      }
    },

    // 判断是否为当前使用的预设
    isCurrentPreset(preset) {
      return this.configForm.theme_mode === preset.config.theme_mode &&
             this.configForm.theme_primary_color === preset.config.theme_primary_color &&
             this.configForm.theme_dark_bg_color === preset.config.theme_dark_bg_color &&
             this.configForm.theme_dark_text_color === preset.config.theme_dark_text_color
    },

    // 应用主题预设
    applyPreset(preset) {
      this.configForm.theme_mode = preset.config.theme_mode
      this.configForm.theme_primary_color = preset.config.theme_primary_color
      this.configForm.theme_dark_bg_color = preset.config.theme_dark_bg_color
      this.configForm.theme_dark_text_color = preset.config.theme_dark_text_color
      
      this.$message.success(`已应用 ${preset.name} 主题预设`)
    }
  }
}
</script>

<style scoped>
.config-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.config-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.card-header i {
  margin-right: 8px;
  color: #409EFF;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Tab样式 */
.config-tabs {
  border: none;
  box-shadow: none;
}

.config-tabs >>> .el-tabs__header {
  background: #fff;
  padding: 0;
  margin: 0;
}

.config-tabs >>> .el-tabs__item {
  height: 50px;
  line-height: 50px;
  font-size: 14px;
  font-weight: 500;
}

.config-tabs >>> .el-tabs__content {
  padding: 30px;
  background: #fff;
}

.tab-icon {
  display: none;
}

/* 表单样式 */
.config-form {
  max-width: 800px;
}

.config-form >>> .el-form-item {
  margin-bottom: 28px;
}

.config-form >>> .el-form-item__label {
  font-weight: 500;
  color: #606266;
}

.config-form >>> .el-radio {
  margin-right: 30px;
  padding: 8px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  transition: all 0.3s;
}

.config-form >>> .el-radio:hover {
  border-color: #409EFF;
  background: #ecf5ff;
}

.config-form >>> .el-radio.is-checked {
  border-color: #409EFF;
  background: #ecf5ff;
}

.config-form >>> .el-radio i {
  margin-right: 4px;
}

.color-value {
  margin-left: 15px;
  font-family: 'Monaco', 'Consolas', monospace;
  color: #606266;
  padding: 4px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
}

.form-tip {
  font-size: 13px;
  color: #909399;
  margin-top: 8px;
  line-height: 1.6;
  padding-left: 4px;
}

/* 主题预设样式 */
.theme-presets {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 8px;
}

.preset-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #fff;
  border: 2px solid #e4e7ed;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  overflow: hidden;
}

.preset-item:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
  transform: translateY(-2px);
}

.preset-item.active {
  border-color: #409EFF;
  background: linear-gradient(135deg, #ecf5ff 0%, #f0f9ff 100%);
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.25);
}

.preset-preview {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s;
}

.preset-item:hover .preset-preview {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.preset-preview i {
  font-size: 26px;
  color: white;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.preset-info {
  flex: 1;
  min-width: 0;
}

.preset-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.preset-item.active .preset-name {
  color: #409EFF;
}

.preset-desc {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preset-check {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 20px;
  color: #409EFF;
  font-weight: bold;
  animation: checkIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes checkIn {
  0% {
    transform: scale(0) rotate(-45deg);
    opacity: 0;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

/* 响应式调整 */
@media screen and (max-width: 1400px) {
  .theme-presets {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 768px) {
  .theme-presets {
    grid-template-columns: 1fr;
  }
}

/* 二维码上传样式 */
.qrcode-uploader {
  display: inline-block;
}

.qrcode-uploader >>> .el-upload {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  background: #fafafa;
}

.qrcode-uploader >>> .el-upload:hover {
  border-color: #409EFF;
  background: #f5f9ff;
}

.upload-placeholder {
  text-align: center;
  color: #8c939d;
}

.upload-placeholder i {
  font-size: 32px;
  margin-bottom: 8px;
  display: block;
}

.upload-text {
  font-size: 13px;
  color: #909399;
}

.qrcode-preview {
  width: 160px;
  height: 160px;
  display: block;
  object-fit: contain;
}

/* Logo上传样式 */
.logo-upload-area {
  display: inline-block;
}

.logo-upload-btn {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  width: 360px;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  background: #fafafa;
}

.logo-upload-btn:hover {
  border-color: #409EFF;
  background: #f5f9ff;
}

.logo-upload-btn i {
  font-size: 32px;
  color: #8c939d;
  margin-bottom: 8px;
}

.upload-hint {
  font-size: 12px;
  color: #b0b3b8;
  margin-top: 4px;
}

.logo-preview-box {
  position: relative;
  width: 360px;
  height: 120px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.logo-actions {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s;
}

.logo-preview-box:hover .logo-actions {
  opacity: 1;
}

/* Logo裁剪对话框样式 */
.cropper-container {
  display: flex;
  gap: 24px;
  min-height: 500px;
}

.cropper-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cropper-area {
  flex: 1;
  background: #f5f7fa;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}

.cropper-area >>> .vue-cropper {
  width: 100%;
  height: 100%;
}

.cropper-tools {
  display: flex;
  justify-content: center;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
}

.cropper-preview-area {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  padding-bottom: 12px;
  border-bottom: 2px solid #e4e7ed;
}

.preview-title i {
  color: #409EFF;
  margin-right: 6px;
}

.preview-box {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e4e7ed;
}

.preview-wrapper {
  width: 240px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin: 0 auto;
}

.preview-info {
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.preview-info p {
  margin: 0;
  padding: 8px 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  border-bottom: 1px solid #f0f0f0;
}

.preview-info p:last-child {
  border-bottom: none;
}

.preview-info i {
  color: #409EFF;
  margin-right: 6px;
}

.logo-uploader {
  display: inline-block;
}

.logo-uploader >>> .el-upload {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 300px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  background: #fafafa;
}

.logo-uploader >>> .el-upload:hover {
  border-color: #409EFF;
  background: #f5f9ff;
}

.logo-preview {
  max-width: 280px;
  max-height: 100px;
  display: block;
  object-fit: contain;
  /* 添加棋盘格背景，显示PNG透明效果 */
  background-image: 
    linear-gradient(45deg, #f0f0f0 25%, transparent 25%), 
    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #f0f0f0 75%), 
    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  background-color: white;
  padding: 10px;
  border-radius: 4px;
}

/* Logo预览容器 */
.logo-preview-container {
  background: #f5f7fa;
  padding: 30px;
  border-radius: 8px;
  margin-top: 20px;
  border: 1px solid #e4e7ed;
}

.logo-preview-header {
  background: white;
  padding: 20px 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.preview-logo-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}

.preview-logo-img {
  max-width: 200px;
  max-height: 60px;
  object-fit: contain;
  /* 添加棋盘格背景，显示PNG透明效果 */
  background-image: 
    linear-gradient(45deg, #f5f7fa 25%, transparent 25%), 
    linear-gradient(-45deg, #f5f7fa 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #f5f7fa 75%), 
    linear-gradient(-45deg, transparent 75%, #f5f7fa 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0px;
  background-color: white;
  padding: 8px;
  border-radius: 4px;
}

.preview-logo-placeholder {
  width: 120px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 18px;
}

.preview-logo-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-site-name {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.preview-site-slogan {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

/* 预览容器 */
.preview-container {
  background: #f5f7fa;
  padding: 30px;
  border-radius: 8px;
  margin-top: 20px;
  border: 1px solid #e4e7ed;
}

.preview-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-list.two-columns {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.preview-item {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: all 0.3s;
}

.preview-item:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  transform: translateY(-2px);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.preview-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.preview-tags {
  display: flex;
  gap: 8px;
}

.preview-tag {
  padding: 4px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.preview-body {
  display: flex;
  gap: 16px;
}

.preview-cover {
  width: 140px;
  height: 105px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  flex-shrink: 0;
}

.preview-content {
  flex: 1;
}

.preview-desc {
  font-size: 14px;
  color: #606266;
  line-height: 1.8;
}

/* 网格预览 */
.preview-grid {
  display: grid;
  gap: 20px;
}

.preview-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: all 0.3s;
}

.preview-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  transform: translateY(-4px);
}

.preview-card-cover {
  width: 100%;
  height: 140px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.preview-card-title {
  padding: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

/* 响应式调整 */
@media screen and (max-width: 1200px) {
  .config-form {
    max-width: 100%;
  }
}

/* 管理后台深色模式样式 */
.admin-dark-mode .config-card {
  background-color: #2a2a2a;
  border-color: #404040;
}

.admin-dark-mode .card-header {
  color: #e0e0e0;
}

.admin-dark-mode >>> .el-tabs__header {
  background: #2a2a2a;
}

.admin-dark-mode >>> .el-tabs__content {
  background: #2a2a2a;
}

.admin-dark-mode >>> .el-tabs__item {
  color: #b0b0b0;
}

.admin-dark-mode >>> .el-tabs__item.is-active {
  color: #409EFF;
}

.admin-dark-mode >>> .el-tabs__active-bar {
  background-color: #409EFF;
}

.admin-dark-mode >>> .el-divider__text {
  background-color: #2a2a2a;
  color: #b0b0b0;
}

.admin-dark-mode >>> .el-form-item__label {
  color: #b0b0b0;
}

.admin-dark-mode .form-tip {
  color: #909399;
}

.admin-dark-mode .color-value {
  color: #b0b0b0;
}

.admin-dark-mode >>> .el-radio__label {
  color: #b0b0b0;
}

.admin-dark-mode >>> .el-radio__input.is-checked + .el-radio__label {
  color: #409EFF;
}

.admin-dark-mode .preview-container {
  border-color: #404040;
  background: #1a1a1a;
}

.admin-dark-mode .preview-item,
.admin-dark-mode .preview-card {
  background-color: #2a2a2a;
  border-color: #404040;
}

.admin-dark-mode .preview-title,
.admin-dark-mode .preview-card-title {
  color: #e0e0e0;
}

.admin-dark-mode .preview-desc {
  color: #909399;
}

.admin-dark-mode .upload-placeholder {
  background-color: #1a1a1a;
  border-color: #404040;
  color: #909399;
}

/* 管理后台深色模式 - 主题预设 */
.admin-dark-mode .preset-item {
  background-color: #2a2a2a;
  border-color: #404040;
}

.admin-dark-mode .preset-item:hover {
  border-color: #409EFF;
  background-color: #303030;
}

.admin-dark-mode .preset-item.active {
  border-color: #409EFF;
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.15) 0%, rgba(64, 158, 255, 0.08) 100%);
}

.admin-dark-mode .preset-name {
  color: #e0e0e0;
}

.admin-dark-mode .preset-item.active .preset-name {
  color: #409EFF;
}

.admin-dark-mode .preset-desc {
  color: #909399;
}
</style>
